import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ValuationFormData, ValuationResult } from '@/types'

interface ValuationStore {
  formData: Partial<ValuationFormData>
  currentStep: number
  result: ValuationResult | null
  isLoading: boolean
  error: string | null

  updateFormData: (data: Partial<ValuationFormData>) => void
  setCurrentStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void
  resetForm: () => void
  setResult: (result: ValuationResult | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  isStepValid: (step: number) => boolean
}

const TOTAL_STEPS = 5

const initialFormData: Partial<ValuationFormData> = {
  propertyType: 'appartamento',
  area: 0,
  zoneName: '',
  hasElevator: false,
  hasPertinenza: false,
  hasGoodExposure: false,
  hasExternalSpaces: false,
  hasGarage: false,
  hasPool: false,
  isResidence: false,
  gdprConsent: false,
}

export const useValuationStore = create<ValuationStore>()(
  persist(
    (set, get) => ({
      formData: initialFormData,
      currentStep: 1,
      result: null,
      isLoading: false,
      error: null,

      updateFormData: (data) =>
        set((state) => ({
          formData: { ...state.formData, ...data },
        })),

      setCurrentStep: (step) => set({ currentStep: step }),

      nextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, TOTAL_STEPS),
        })),

      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 1),
        })),

      resetForm: () =>
        set({
          formData: initialFormData,
          currentStep: 1,
          result: null,
          error: null,
        }),

      setResult: (result) => set({ result }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      isStepValid: (step) => {
        const { formData } = get()

        switch (step) {
          case 1: // Zona
            return !!(
              formData.zoneName
            )

          case 2: // Proprieta base
            return !!(
              formData.propertyType &&
              formData.area &&
              formData.area > 0
            )

          case 3: // Caratteristiche
            return true // Opzionale

          case 4: // Servizi
            return true // Opzionale

          case 5: // Contatto
            return !!(
              formData.email &&
              formData.email.includes('@') &&
              formData.gdprConsent === true
            )

          default:
            return false
        }
      },
    }),
    {
      name: 'valuation-sanremo',
      partialize: (state) => ({
        formData: state.formData,
        currentStep: state.currentStep,
      }),
    }
  )
)
